import type { Ref } from "vue";
import { isPackaged } from "@lemmonhq/orders";
//const isPackaged = true;
import { instrumentation } from "@lemmonhq/analytics";
import { hideProgressBar, showProgressBar } from "./progress-bar";
import {
  reportNetworkFetchError,
  reportNetworkFetchSuccess,
} from "@app/vue/providers/NetworkStatus";
export type RoutingServerId = string;
export type RoutingConfig = {
  mode: "auto" | RoutingServerId[];
  forceServerId?: string | null;
  order: RoutingServerId[];
  servers: Record<RoutingServerId, { baseUrl: string }>;
  timeoutMs?: number;
  maxAttempts?: number;
};

const fallbackOrigin =
  typeof window !== "undefined" && window.location
    ? window.location.origin
    : "http://localhost";
const shouldShowProgressBar = true; // && import.meta.env.VITE_ENV === "staging";

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === "AbortError";

// Normalise an abort reason into something callers can identify with
// `isAbortError` / `err.name === "AbortError"`, matching what fetch throws.
const toAbortError = (reason: unknown): unknown =>
  reason instanceof DOMException && reason.name === "AbortError"
    ? reason
    : new DOMException(
        typeof reason === "string" ? reason : "The operation was aborted.",
        "AbortError",
      );

// Mirror an abort from `source` onto `target`, returning a detach function.
// Each failover attempt gets its own controller, so the listener must be
// removed when the attempt settles or a long-lived caller signal (a payment
// retry controller, say) accumulates one listener per request.
const forwardAbort = (
  source: AbortSignal | null | undefined,
  target: AbortController,
): (() => void) => {
  if (!source) return () => {};
  if (source.aborted) {
    target.abort(source.reason);
    return () => {};
  }
  const onAbort = () => target.abort(source.reason);
  source.addEventListener("abort", onAbort, { once: true });
  return () => source.removeEventListener("abort", onAbort);
};

const shouldRetry = (response?: Response): boolean =>
  !!response && response.status > 500 && response.status < 600;

const trackServerError = (
  serverId: string,
  targetUrl: string,
  statusCode: number,
) => {
  instrumentation.track("network_fetch_server_error", {
    server_id: serverId,
    target_host: new URL(targetUrl).host,
    status_code: statusCode,
  });
};

const trackNetworkError = (serverId: string, targetUrl: string) => {
  instrumentation.track("network_fetch_network_error", {
    server_id: serverId,
    target_host: new URL(targetUrl).host,
  });
};

const buildTargetUrl = (baseUrl: string, requestUrl: string): string => {
  const resolvedBase = baseUrl === "/" ? fallbackOrigin : baseUrl;
  const parsedRequestUrl = new URL(requestUrl, fallbackOrigin);
  const parsedBase = new URL(resolvedBase, fallbackOrigin);
  return new URL(
    `${parsedRequestUrl.pathname}${parsedRequestUrl.search}${parsedRequestUrl.hash}`,
    parsedBase,
  ).toString();
};

const getServerSequence = (config: RoutingConfig): RoutingServerId[] => {
  const requestedServers =
    config.mode === "auto" ? config.order : config.mode || [];
  const forced = config.forceServerId ? [config.forceServerId] : [];
  const uniqueServers = Array.from(
    new Set([...forced, ...requestedServers].filter(Boolean)),
  ).filter((id) => !!config.servers[id]);

  return config.maxAttempts
    ? uniqueServers.slice(0, config.maxAttempts)
    : uniqueServers;
};

const cloneRequestBody = async (
  request: Request,
): Promise<ArrayBuffer | undefined> => {
  if (request.method === "GET" || request.method === "HEAD") return undefined;
  const cloned = request.clone();
  return cloned.body ? cloned.arrayBuffer() : undefined;
};

export const createRoutingFetch = (config: Ref<RoutingConfig>) => {
  return async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    if (shouldShowProgressBar) showProgressBar();
    try {
      const baseRequest = new Request(input, init);
      const serverIds = getServerSequence(config.value);

      if (serverIds.length === 0) {
        try {
          const response = await globalThis.fetch(baseRequest);
          reportNetworkFetchSuccess();
          return response;
        } catch (error) {
          if (!isAbortError(error)) {
            reportNetworkFetchError();
            trackNetworkError("direct", baseRequest.url);
          }
          throw error;
        }
      }

      const bodyBuffer = await cloneRequestBody(baseRequest);
      const callerSignal = baseRequest.signal;
      let lastError: unknown;

      for (const serverId of serverIds) {
        const server = config.value.servers[serverId];
        if (!server) continue;

        // The caller's signal is the one that means "stop" (retry cancellation,
        // poll timeouts, unmount). Honour it before every attempt so an abort
        // that lands mid-failover does not silently start the next request.
        if (callerSignal?.aborted) {
          throw toAbortError(callerSignal.reason);
        }

        const targetUrl = buildTargetUrl(server.baseUrl, baseRequest.url);
        const controller = new AbortController();
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        // Chain the caller's signal into this attempt's controller. Each
        // attempt builds a fresh Request, so the caller's signal cannot be
        // passed straight through -- without this it is dropped entirely.
        const detachCaller = forwardAbort(callerSignal, controller);

        if (config.value.timeoutMs) {
          timeoutId = setTimeout(
            () => controller.abort("timeout"),
            config.value.timeoutMs,
          );
        }

        try {
          const attemptRequest = new Request(targetUrl, {
            method: baseRequest.method,
            headers: baseRequest.headers,
            body: bodyBuffer,
            cache: baseRequest.cache,
            credentials: "include",
            mode: baseRequest.mode,
            redirect: baseRequest.redirect,
            signal: controller.signal,
          });

          const response = await globalThis.fetch(attemptRequest);

          // A response (even a 5xx one) means the network itself is fine.
          reportNetworkFetchSuccess();

          if (shouldRetry(response)) {
            trackServerError(serverId, targetUrl, response.status);
            lastError = new Error(
              `Server ${serverId} responded with ${response.status}`,
            );
            continue;
          }

          return response;
        } catch (error) {
          // Surface the caller's abort reason rather than the generic fetch
          // AbortError, so callers can tell "we cancelled this" from "the
          // routing timeout fired". Neither abort fails over: that is
          // pre-existing behaviour of `config.timeoutMs`, left as-is.
          if (callerSignal?.aborted) throw toAbortError(callerSignal.reason);
          if (isAbortError(error)) throw error;
          reportNetworkFetchError();
          trackNetworkError(serverId, targetUrl);
          lastError = error;
        } finally {
          if (timeoutId) clearTimeout(timeoutId);
          detachCaller();
        }
      }

      if (lastError) throw lastError;
      try {
        const response = await globalThis.fetch(baseRequest);
        reportNetworkFetchSuccess();
        return response;
      } catch (error) {
        if (!isAbortError(error)) {
          reportNetworkFetchError();
          trackNetworkError("direct", baseRequest.url);
        }
        throw error;
      }
    } finally {
      if (shouldShowProgressBar) hideProgressBar();
    }
  };
};
